import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Card';
import { useLocale } from '@/hooks/useLocale';

const LOCALE_TAG = { en: 'en-GB', ar: 'ar-EG' } as const;

/** One figure: its name, its value, a hint underneath and an optional way into its records. */
export function Stat({
  label,
  children,
  hint,
  action,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card className="min-w-0 p-4">
      <p className="text-muted text-sm">{label}</p>
      <div className="text-ink mt-2 text-2xl font-semibold">{children}</div>
      {hint ? <div className="text-muted mt-1 text-xs">{hint}</div> : null}
      {action}
    </Card>
  );
}

/** A titled region, so every group of figures is a landmark a screen reader can jump to. */
export function Section({
  id,
  title,
  note,
  children,
}: {
  id: string;
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h2 id={id} className="text-ink font-semibold">
          {title}
        </h2>
        {note ? <p className="text-muted text-xs">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

/** A whole number in the reader's digits, kept left-to-right inside Arabic text. */
export function CountText({ value }: { value: number }) {
  const { locale } = useLocale();
  return (
    <span dir="ltr" className="tabular-nums">
      {new Intl.NumberFormat(LOCALE_TAG[locale]).format(value)}
    </span>
  );
}

/** A duration in days to one decimal place, or a dash when nothing was timed. */
export function DaysText({ seconds }: { seconds: number | null }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (seconds === null) return <>—</>;
  const format = new Intl.NumberFormat(LOCALE_TAG[locale], {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return (
    <span>
      <span dir="ltr" className="tabular-nums">
        {format.format(seconds / 86_400)}
      </span>{' '}
      <span className="text-muted text-sm font-normal">{t('reports.optical.daysUnit')}</span>
    </span>
  );
}

/** The "view records" link every figure opens its records with. */
export function ViewRecords({ title, onOpen }: { title: string; onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-brand-700 mt-2 text-sm font-medium hover:underline"
      aria-label={t('reports.sales.records.viewFor', { figure: title })}
    >
      {t('reports.sales.records.view')}
    </button>
  );
}

/** Previous and next page controls for a server-paged record list. */
export function RecordPager({
  page,
  pages,
  onPage,
}: {
  page: number;
  pages: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  if (pages <= 1) return null;
  const button =
    'text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50';
  return (
    <div className="mt-3 flex items-center justify-between gap-3 text-sm">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className={button}
      >
        {t('reports.sales.records.previous')}
      </button>
      <span className="text-muted">{t('reports.sales.records.page', { page, pages })}</span>
      <button
        type="button"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
        className={button}
      >
        {t('reports.sales.records.next')}
      </button>
    </div>
  );
}

/** A simple table: header labels, then rows of cells; the end-aligned columns hold numbers. */
export function SimpleTable({
  caption,
  headers,
  rows,
  ends = [],
  fluid = false,
}: {
  caption?: string;
  headers: string[];
  rows: { key: string; cells: ReactNode[] }[];
  ends?: number[];
  /** Two narrow columns that fit a phone without the usual minimum width. */
  fluid?: boolean;
}) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className={`w-full text-sm [&_td]:px-2 [&_th]:px-2 ${fluid ? '' : 'min-w-[20rem]'}`}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr className="text-muted border-line border-b">
            {headers.map((header, index) => (
              <th
                key={`${header}-${index}`}
                className={`py-2 font-medium ${ends.includes(index) ? 'text-end' : 'text-start'}`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-line border-b last:border-b-0">
              {row.cells.map((cell, index) => (
                <td
                  key={index}
                  className={`py-2 ${ends.includes(index) ? 'text-end tabular-nums' : 'break-words'}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
